/**
 * AddBenchmarkButton: the staff Add benchmark action on the Evals page.
 *
 * Staff only, on the team the tab shows: hidden for customers, under
 * impersonation (read-only), and on a restricted demo team. The whole
 * form shows from the start with the GitHub connection on top, which
 * installs and links the TrainLoop GitHub App when the team has no
 * installation yet; the rest stays disabled until it works.
 *
 * The form has four sections: GitHub, Benchmark, Models and Automation.
 * The rest takes its defaults: the trainloop-env adapter and its layout,
 * the judge its world names (shown only when it refuses Add), an empty
 * deny list, and the chain's default training preset. The team's Evals
 * config page changes any of them. Each field says what it is in a tip
 * beside its label; a line under a field carries only live information,
 * such as a derived slug. Add saves everything and starts
 * the benchmark inventory (hooks/useAddBenchmark.ts). The writes and their
 * order live in the hook, the defaults and the config change in
 * addBenchmark.ts.
 */

import { useRef, useState, type FormEvent } from 'react'
import { ChevronRight, ExternalLink, Loader2, Plus } from 'lucide-react'
import { Link } from 'react-router-dom'

import { getApiErrorDetail } from '@/api/client'
import { useListBenchmarkAdaptersApiBenchmarksAdaptersGet } from '@/api/generated/benchmarks/benchmarks'
import {
  useGetTeamEvalsConfigApiInternalTeamsTeamIdEvalsConfigGet,
  useGetTeamGithubAppApiInternalTeamsTeamIdGithubAppGet,
  useListTeamModelsApiInternalTeamsTeamIdModelsGet,
} from '@/api/generated/internal/internal'
import type {
  BenchmarkAdapterItem,
  GithubAppStatus,
  PlatformGithubAppInstallation,
  ProxyModelListItem,
  TeamEvalsConfigResponse,
  TeamModelAdminRow,
} from '@/api/generated/model'
import { teamConfigPath } from '@/components/internal/team-config/paths'
import { TrainingSwitchField } from '@/components/internal/team-config/TrainingFields'
import type { TrainingValues } from '@/components/internal/team-config/training'
import { TextField } from '@/components/internal/team-config/TextField'
import { GithubMark } from '@/components/shared/GithubMark'
import { FieldLabel } from '@/components/shared/InfoTip'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useTeam } from '@/contexts/TeamContext'
import {
  ADD_STEPS,
  useAddBenchmark,
  useGithubInstall,
  useWorldJudge,
  type AddBenchmarkResult,
  type AddStep,
  type GithubInstall,
} from '@/hooks/useAddBenchmark'
import { useProxyModels } from '@/hooks/useModels'
import { usePresetChoices } from '@/hooks/usePresetChoices'
import { describedBy, tipId } from '@/lib/describedBy'
import { deriveSlug } from '@/lib/slug'

import {
  WORLD_NAMES_NO_JUDGE,
  becomesScored,
  judgeBlocksAdd,
  keepsLayout,
  setsDefaultModel,
  initialInput,
  groupDisplayFor,
  modelSlugFor,
  showsModelIdentity,
  withChainPreset,
  type AddBenchmarkInput,
  type WorldJudge,
} from './addBenchmark'

const STEP_LABELS: Record<AddStep, string> = {
  config: 'Save the benchmark',
  model: 'Set the default model',
  inventory: 'Start the benchmark inventory',
}

/** What each field is, shown in the tip beside its label. */
const TIPS = {
  repository: 'The GitHub repository that holds the benchmark tests.',
  branch: 'The branch Add reads the tests from.',
  group: "The eval that holds this benchmark's scores.",
  baseModel:
    "Scoring uses the team's default model. Add keeps its current catalog model unless you choose another.",
  modelSlug:
    'The slug of the team model that runs on the base model. Add reuses a team model with this slug or creates one.',
  modelName: 'The display name Add gives a new team model.',
  scoring: 'Score new tests starts after the import. The failure analysis runs inside it.',
  generating:
    'Each scoring run starts task generation. The task gate checks the generated tasks.',
} as const

export function AddBenchmarkButton(): React.JSX.Element | null {
  const { isEmployee, impersonatingTeamId, isRestricted, activeTeamId } = useTeam()
  const [open, setOpen] = useState(false)
  const titleRef = useRef<HTMLHeadingElement>(null)
  if (!isEmployee || impersonatingTeamId !== null || isRestricted || activeTeamId === null) {
    return null
  }
  return (
    <>
      <Button
        onClick={() => {
          setOpen(true)
        }}
      >
        <Plus className="mr-2 h-4 w-4" />
        Add benchmark
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="flex max-h-[90dvh] flex-col gap-0 overflow-hidden rounded-sm p-0 shadow-none sm:max-w-2xl sm:rounded-sm"
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            titleRef.current?.focus()
          }}
        >
          <DialogHeader className="shrink-0 border-b px-6 py-5 text-left">
            <DialogTitle ref={titleRef} tabIndex={-1} className="outline-none">
              Add benchmark
            </DialogTitle>
            <DialogDescription>
              Add a GitHub benchmark to this team and import its tests.
            </DialogDescription>
          </DialogHeader>
          {open && (
            <AddBenchmarkForm
              teamId={activeTeamId}
              onClose={() => {
                setOpen(false)
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

/** Everything the form reads before it can be filled in. */
function AddBenchmarkForm({
  teamId,
  onClose,
}: {
  teamId: string
  onClose: () => void
}): React.JSX.Element {
  const github = useGetTeamGithubAppApiInternalTeamsTeamIdGithubAppGet(teamId)
  const setup = useGetTeamEvalsConfigApiInternalTeamsTeamIdEvalsConfigGet(teamId)
  const models = useListTeamModelsApiInternalTeamsTeamIdModelsGet(teamId)
  const adapters = useListBenchmarkAdaptersApiBenchmarksAdaptersGet()
  const catalog = useProxyModels()
  const failed =
    [github, setup, models, adapters].find((query) => query.isError) ??
    (catalog.isError ? catalog : undefined)
  if (failed !== undefined) {
    return (
      <p className="text-sm text-destructive" role="alert">
        {getApiErrorDetail(failed.error) ?? 'The team setup could not be read.'}
      </p>
    )
  }
  if (
    github.data?.status !== 200 ||
    setup.data?.status !== 200 ||
    models.data?.status !== 200 ||
    adapters.data?.status !== 200 ||
    catalog.data === undefined
  ) {
    return <p className="text-sm text-muted-foreground">The team setup is loading.</p>
  }
  return (
    <SetupForm
      teamId={teamId}
      github={github.data.data}
      setup={setup.data.data}
      models={models.data.data.models}
      adapters={adapters.data.data.adapters}
      catalog={catalog.data}
      onClose={onClose}
    />
  )
}

export interface SetupFormProps {
  teamId: string
  github: GithubAppStatus
  setup: TeamEvalsConfigResponse
  /** The team's models, for the default model the form starts from. */
  models: readonly TeamModelAdminRow[]
  adapters: readonly BenchmarkAdapterItem[]
  catalog: readonly ProxyModelListItem[]
  onClose: () => void
}

export function SetupForm({
  teamId,
  github,
  setup,
  models,
  adapters,
  catalog,
  onClose,
}: SetupFormProps): React.JSX.Element {
  const [input, setInput] = useState<AddBenchmarkInput>(() =>
    initialInput(window.location.origin, setup, models),
  )
  const [step, setStep] = useState<AddStep | null>(null)
  const add = useAddBenchmark(teamId, setStep)
  const judge = useWorldJudge(teamId, input)
  const routes = (adapters.find((item) => item.name === input.adapter)?.routes ?? []).map(
    (route) => route.name,
  )
  const sent = withChainPreset(input, usePresetChoices(routes).choices)
  const scored = becomesScored(setup, input)
  const locked = add.isPending || add.isSuccess
  // The whole form shows from the start, and everything under the GitHub
  // section stays disabled until the team's installation works: the
  // repository list and every write after it read through it.
  const disabled = locked || github.status !== 'installed'
  // A world with no judge fails scoring and training, so Add stays off
  // until the judge is read and named (`judgeBlocksAdd`). Training on
  // needs a preset, which no stack may offer (TrainingSwitchField says so).
  const refused =
    disabled ||
    judgeBlocksAdd(judge) ||
    (scored && sent.generating && sent.training && sent.trainingPreset === null)
  const update = (changes: Partial<AddBenchmarkInput>): void => {
    setInput((previous) => ({ ...previous, ...changes }))
  }
  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault()
    if (refused) return
    add.mutate({ input: sent, adapters, catalog })
  }
  const setsModel = setsDefaultModel(setup, scored)
  const fields = { input, disabled, update }
  return (
    <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 space-y-8 overflow-y-auto px-6 py-6">
        <GithubSection teamId={teamId} github={github} />
        <BenchmarkFields {...fields} github={github} judge={judge} keepsLayout={keepsLayout(setup)} />
        {setsModel && <ModelFields {...fields} catalog={catalog} models={models} />}
        {scored && <AutomationFields {...fields} routes={routes} />}
        <AddOutcome step={step} input={input} setsModel={setsModel} add={add} />
      </div>
      <DialogFooter className="shrink-0 border-t px-6 py-4">
        <Button type="button" variant="outline" disabled={add.isPending} onClick={onClose}>
          {add.isSuccess ? 'Close' : 'Cancel'}
        </Button>
        {!add.isSuccess && (
          <Button type="submit" disabled={refused}>
            {add.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Add benchmark
          </Button>
        )}
      </DialogFooter>
    </form>
  )
}

/** One titled section of the form. */
function FormSection({
  id,
  title,
  children,
}: {
  id: string
  title: string
  children: React.ReactNode
}): React.JSX.Element {
  return (
    <section className="space-y-4" aria-labelledby={`${id}-heading`}>
      <h3 id={`${id}-heading`} className="font-serif text-base">
        {title}
      </h3>
      {children}
    </section>
  )
}

/** The team's GitHub App installation, or the install button and list that link one. */
export function GithubSection({
  teamId,
  github,
}: {
  teamId: string
  github: GithubAppStatus
}): React.JSX.Element {
  if (github.status === 'installed' && github.installation !== null) {
    return (
      <FormSection id="add-benchmark-github" title="GitHub">
        <p className="text-sm text-muted-foreground">
          Installation{' '}
          <span className="font-mono text-foreground">{github.installation.installation_id}</span>{' '}
          of the GitHub App on {github.installation.account} covers{' '}
          {github.installation.repository_count} repositories.
        </p>
        <ConnectionsLink teamId={teamId} />
      </FormSection>
    )
  }
  if (github.status !== 'not_connected') {
    return (
      <FormSection id="add-benchmark-github" title="GitHub">
        <p className="text-sm text-destructive" role="alert">
          {github.detail ?? 'The team GitHub App installation cannot be reached.'}
        </p>
        <ConnectionsLink teamId={teamId} />
      </FormSection>
    )
  }
  return (
    <FormSection id="add-benchmark-github" title="GitHub">
      <ConnectGithub teamId={teamId} installUrl={github.install_url} />
    </FormSection>
  )
}

/**
 * For a team with no installation: the button that installs the App on
 * GitHub, and the App's installations, any of which links with one click
 * (useGithubInstall). The new installation links on its own.
 */
function ConnectGithub({
  teamId,
  installUrl,
}: {
  teamId: string
  installUrl: string | null
}): React.JSX.Element {
  const install = useGithubInstall(teamId)
  const { connect } = install
  const [listed, setListed] = useState(false)
  return (
    <>
      <p className="text-sm text-muted-foreground">
        Install the TrainLoop GitHub App on the organization that owns the benchmark repository.
        This dialog links the new installation once GitHub reports it.
      </p>
      <Collapsible open={listed} onOpenChange={setListed} className="space-y-4">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {installUrl !== null && (
            <Button asChild variant="outline">
              <a
                href={installUrl}
                target="_blank"
                rel="noreferrer"
                onClick={() => {
                  void install.start()
                }}
              >
                <GithubMark />
                Install the GitHub App
                <ExternalLink aria-hidden="true" />
              </a>
            </Button>
          )}
          <CollapsibleTrigger className="group flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ChevronRight
              className="h-4 w-4 transition-transform group-data-[state=open]:rotate-90"
              aria-hidden="true"
            />
            {installUrl === null
              ? 'Link an organization that has the App'
              : 'or link an organization that has the App'}
          </CollapsibleTrigger>
        </div>
        <InstallProgress waiting={install.waiting} connect={connect} />
        <CollapsibleContent>
          <InstallationChoices
            install={install}
            onLink={(installationId) => {
              connect.mutate({ installationId })
            }}
          />
        </CollapsibleContent>
      </Collapsible>
    </>
  )
}

/** The App's installations to link from, or why they are not listed yet. */
function InstallationChoices({
  install,
  onLink,
}: {
  install: GithubInstall
  onLink: (installationId: number) => void
}): React.JSX.Element | null {
  if (install.listError !== null) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-destructive" role="alert">
          {getApiErrorDetail(install.listError) ??
            'The installations of the GitHub App could not be listed.'}
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={install.listing}
          onClick={install.retry}
        >
          {install.listing && <Loader2 className="h-4 w-4 animate-spin" />}
          Try again
        </Button>
      </div>
    )
  }
  if (install.installations === undefined) {
    return (
      <p className="text-sm text-muted-foreground">The GitHub App installations are loading.</p>
    )
  }
  if (install.installations.length === 0) {
    return <p className="text-sm text-muted-foreground">No organization has the App yet.</p>
  }
  return (
    <InstallationList
      installations={install.installations}
      disabled={install.connect.isPending}
      onLink={onLink}
    />
  )
}

/** What the link is doing: waiting for GitHub, linking, or refused. */
function InstallProgress({
  waiting,
  connect,
}: {
  waiting: boolean
  connect: GithubInstall['connect']
}): React.JSX.Element | null {
  if (connect.isError) {
    return (
      <p className="text-sm text-destructive" role="alert">
        {getApiErrorDetail(connect.error) ?? 'The installation was not linked.'}
      </p>
    )
  }
  if (!connect.isPending && !waiting) return null
  return (
    <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      {connect.isPending
        ? 'The dialog is linking the installation.'
        : 'The dialog is waiting for GitHub to report the installation.'}
    </p>
  )
}

/** The App's installations, newest first, each with its Link button. */
export function InstallationList({
  installations,
  disabled,
  onLink,
}: {
  installations: readonly PlatformGithubAppInstallation[]
  disabled: boolean
  onLink: (installationId: number) => void
}): React.JSX.Element {
  return (
    <ul
      className="max-h-48 divide-y divide-border overflow-y-auto border border-border"
      aria-label="Organizations that have the App"
    >
        {installations.map((installation) => (
          <li
            key={installation.installation_id}
            className="flex items-center justify-between gap-4 px-3 py-2"
          >
            <span className="font-mono text-sm">{installation.account}</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              aria-label={`Link ${installation.account}`}
              onClick={() => {
                onLink(installation.installation_id)
              }}
            >
              Link
            </Button>
          </li>
        ))}
    </ul>
  )
}

/** Where staff change or relink a team's installation: one per team, so not here. */
function ConnectionsLink({ teamId }: { teamId: string }): React.JSX.Element {
  return (
    <p className="text-xs text-muted-foreground">
      Change it on the team's{' '}
      <Link
        to={teamConfigPath(teamId, 'connections')}
        className="text-foreground underline underline-offset-4 hover:text-primary"
      >
        Connections
      </Link>{' '}
      page.
    </p>
  )
}

interface FieldsProps {
  input: AddBenchmarkInput
  disabled: boolean
  update: (changes: Partial<AddBenchmarkInput>) => void
}

function BenchmarkFields({
  input,
  github,
  judge,
  keepsLayout,
  disabled,
  update,
}: FieldsProps & {
  github: GithubAppStatus
  judge: WorldJudge
  keepsLayout: boolean
}): React.JSX.Element {
  const repositories = github.installation?.repositories ?? []
  return (
    <FormSection id="add-benchmark-benchmark" title="Benchmark">
      <SelectField
        id="add-benchmark-repo"
        label="Repository"
        tip={TIPS.repository}
        value={input.repo}
        placeholder={
          github.status === 'installed'
            ? 'Pick a repository'
            : 'Connect GitHub to list the repositories'
        }
        options={repositories.map((repo) => ({
          value: repo.full_name,
          label: repo.full_name,
        }))}
        disabled={disabled}
        onChange={(repo) => {
          const picked = repositories.find((item) => item.full_name === repo)
          update({
            repo,
            ref: picked?.default_branch ?? '',
            groupDisplay: groupDisplayFor(repo),
          })
        }}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="add-benchmark-ref"
          label="Branch"
          tip={TIPS.branch}
          value={input.ref}
          mono
          disabled={disabled}
          onChange={(ref) => {
            update({ ref })
          }}
        />
        <TextField
          id="add-benchmark-group"
          label="Eval name"
          tip={TIPS.group}
          value={input.groupDisplay}
          disabled={disabled}
          hint={
            input.groupDisplay.trim() === ''
              ? undefined
              : `Its slug is ${deriveSlug(input.groupDisplay)}.`
          }
          onChange={(groupDisplay) => {
            update({ groupDisplay })
          }}
        />
      </div>
      <JudgeNote judge={judge} />
      {keepsLayout && (
        <p className="text-sm text-muted-foreground">
          This team already has an eval PR layout, which Add keeps. The night keeps scoring the
          benchmark it scores now, so a different benchmark is listed beside it and leaves the
          default model alone unless the team has none.
        </p>
      )}
    </FormSection>
  )
}

/** The team's default model, when Add sets it (`setsDefaultModel`). */
function ModelFields({
  input,
  catalog,
  models,
  disabled,
  update,
}: FieldsProps & {
  catalog: readonly ProxyModelListItem[]
  models: readonly TeamModelAdminRow[]
}): React.JSX.Element {
  return (
    <FormSection id="add-benchmark-models" title="Models">
      <SelectField
        id="add-benchmark-base-model"
        label="Base model"
        tip={TIPS.baseModel}
        value={input.baseModel}
        placeholder="The current default runs on a custom endpoint"
        options={catalog.map((item) => ({
          value: item.slug,
          label: `${item.display_name} (${item.slug})`,
        }))}
        disabled={disabled}
        onChange={(baseModel) => {
          update({ baseModel, modelSlug: modelSlugFor(baseModel) })
        }}
      />
      {showsModelIdentity(input, models) && (
        <ModelIdentityFields input={input} catalog={catalog} disabled={disabled} update={update} />
      )}
    </FormSection>
  )
}

/**
 * Why the world's judge holds Add back, if it does: the dialog is reading
 * world.json, the world names no judge, or the api cannot read it. A
 * named judge shows nothing here; the Evals config page shows it.
 */
export function JudgeNote({ judge }: { judge: WorldJudge }): React.JSX.Element | null {
  switch (judge.kind) {
    case 'named':
    case 'unpicked':
      return null
    case 'reading':
      return (
        <p className="text-xs text-muted-foreground" role="status">
          The dialog is reading the judge from world.json.
        </p>
      )
    case 'unnamed':
      return (
        <p className="text-xs text-destructive" role="alert">
          {WORLD_NAMES_NO_JUDGE}
        </p>
      )
    case 'unreadable':
      return (
        <p className="text-xs text-destructive" role="alert">
          {judge.detail}
        </p>
      )
  }
}

/** The team model's slug and name, shown only where they apply (`showsModelIdentity`). */
function ModelIdentityFields({
  input,
  catalog,
  disabled,
  update,
}: FieldsProps & { catalog: readonly ProxyModelListItem[] }): React.JSX.Element {
  const base = catalog.find((item) => item.slug === input.baseModel)
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <TextField
        id="add-benchmark-model-slug"
        label="Model slug"
        tip={TIPS.modelSlug}
        value={input.modelSlug}
        mono
        disabled={disabled}
        onChange={(modelSlug) => {
          update({ modelSlug })
        }}
      />
      <TextField
        id="add-benchmark-model-name"
        label="Model name"
        tip={TIPS.modelName}
        value={input.modelDisplay}
        placeholder={base?.display_name}
        disabled={disabled}
        onChange={(modelDisplay) => {
          update({ modelDisplay })
        }}
      />
    </div>
  )
}

/** The training values as TrainingFields reads them. */
function trainingValues(input: AddBenchmarkInput): TrainingValues {
  return {
    enabled: input.generating && input.training,
    preset: input.trainingPreset,
    maxSteps: input.trainingMaxSteps,
  }
}

/** A TrainingFields change as a change to the form. */
function trainingChanges(changes: Partial<TrainingValues>): Partial<AddBenchmarkInput> {
  return {
    ...(changes.enabled === undefined ? {} : { training: changes.enabled }),
    ...(changes.preset === undefined ? {} : { trainingPreset: changes.preset }),
    ...(changes.maxSteps === undefined ? {} : { trainingMaxSteps: changes.maxSteps }),
  }
}

/**
 * The team switches the chain starts its jobs by, written with the scored
 * benchmark. Training trains what the gate proves, so its switch is off
 * while task generation is.
 */
function AutomationFields({
  input,
  routes,
  disabled,
  update,
}: FieldsProps & { routes: readonly string[] }): React.JSX.Element {
  return (
    <FormSection id="add-benchmark-automation" title="Automation">
      <SwitchField
        id="add-benchmark-scoring"
        label="Score this benchmark"
        tip={TIPS.scoring}
        checked={input.scoring}
        disabled={disabled}
        onChange={(scoring) => {
          update({ scoring })
        }}
      />
      <div className="space-y-2">
        <SwitchField
          id="add-benchmark-generating"
          label="Generate training tasks"
          tip={TIPS.generating}
          checked={input.generating}
          disabled={disabled}
          onChange={(generating) => {
            update({ generating })
          }}
        />
        <p className="text-xs text-muted-foreground">
          The task gate spends about $2 on each task it proves.
        </p>
      </div>
      <TrainingSwitchField
        idPrefix="add-benchmark"
        values={trainingValues(input)}
        routes={routes}
        disabled={disabled || !input.generating}
        onChange={(changes) => {
          update(trainingChanges(changes))
        }}
      />
    </FormSection>
  )
}

function SwitchField({
  id,
  label,
  tip,
  checked,
  disabled,
  onChange,
}: {
  id: string
  label: string
  tip: string
  checked: boolean
  disabled: boolean
  onChange: (checked: boolean) => void
}): React.JSX.Element {
  return (
    <div className="flex items-center gap-3">
      <Switch
        id={id}
        checked={checked}
        disabled={disabled}
        aria-describedby={tipId(id)}
        onCheckedChange={onChange}
      />
      <FieldLabel htmlFor={id} label={label} tip={tip} />
    </div>
  )
}

interface SelectOption {
  value: string
  label: string
}

function SelectField({
  id,
  label,
  tip,
  value,
  options,
  onChange,
  placeholder,
  hint,
  disabled,
}: {
  id: string
  label: string
  tip: string
  value: string
  options: readonly SelectOption[]
  onChange: (value: string) => void
  placeholder?: string
  /** A live line under the field. */
  hint?: string
  disabled: boolean
}): React.JSX.Element {
  const hintId = `${id}-hint`
  return (
    <div className="space-y-2">
      <FieldLabel htmlFor={id} label={label} tip={tip} />
      <Select value={value} disabled={disabled} onValueChange={onChange}>
        <SelectTrigger
          id={id}
          className="font-mono"
          aria-describedby={describedBy(tipId(id), hint !== undefined && hintId)}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value} className="font-mono">
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hint !== undefined && (
        <p id={hintId} className="text-xs text-muted-foreground">
          {hint}
        </p>
      )}
    </div>
  )
}

/** The step list, the refusal and what happens next: everything under the fields. */
function AddOutcome({
  step,
  input,
  setsModel,
  add,
}: {
  step: AddStep | null
  input: AddBenchmarkInput
  setsModel: boolean
  add: ReturnType<typeof useAddBenchmark>
}): React.JSX.Element {
  return (
    <div className="space-y-4">
      {step !== null && (
        <StepList
          step={step}
          setsModel={setsModel}
          failed={add.isError}
          succeeded={add.isSuccess}
        />
      )}
      {add.isError && (
        <p className="text-sm text-destructive" role="alert">
          {getApiErrorDetail(add.error) ?? add.error.message}
        </p>
      )}
      {add.data !== undefined && <AddedNote result={add.data} scoring={input.scoring} />}
    </div>
  )
}

type StepState = 'waiting' | 'running' | 'done' | 'failed' | 'skipped'

const STATE_LABELS: Record<StepState, string> = {
  waiting: 'Waiting',
  running: 'Running',
  done: 'Done',
  failed: 'Failed',
  skipped: 'Not needed',
}

/** Where each step stands, given the step that last started. */
function stepState(
  step: AddStep,
  current: AddStep,
  failed: boolean,
  succeeded: boolean,
  setsModel: boolean,
): StepState {
  if (step === 'model' && !setsModel) return 'skipped'
  if (succeeded) return 'done'
  const order = ADD_STEPS.indexOf(step) - ADD_STEPS.indexOf(current)
  if (order < 0) return 'done'
  if (order > 0) return 'waiting'
  return failed ? 'failed' : 'running'
}

function StepList({
  step,
  setsModel,
  failed,
  succeeded,
}: {
  step: AddStep
  /** The model step runs (`setsDefaultModel`). */
  setsModel: boolean
  failed: boolean
  succeeded: boolean
}): React.JSX.Element {
  return (
    <ol className="space-y-1 text-sm" aria-label="What Add does">
      {ADD_STEPS.map((name) => {
        const state = stepState(name, step, failed, succeeded, setsModel)
        return (
          <li key={name} className="flex justify-between gap-4">
            <span>{STEP_LABELS[name]}</span>
            <span
              className={
                state === 'failed'
                  ? 'font-mono text-xs text-destructive'
                  : 'font-mono text-xs text-muted-foreground'
              }
            >
              {STATE_LABELS[state]}
            </span>
          </li>
        )
      })}
    </ol>
  )
}

/** What happens after Add, given whether the benchmark became scored and scoring is on. */
function nextStep(result: AddBenchmarkResult, scoring: boolean): string {
  if (!result.scored) return 'The night keeps scoring the team’s current benchmark.'
  if (scoring) return 'Score new tests starts when the import finishes.'
  return 'The night scores this benchmark once scoring is on.'
}

function AddedNote({
  result,
  scoring,
}: {
  result: AddBenchmarkResult
  scoring: boolean
}): React.JSX.Element {
  return (
    <p className="text-sm text-muted-foreground" role="status">
      The benchmark inventory started as <span className="font-mono">{result.jobName}</span>.{' '}
      {nextStep(result, scoring)}
    </p>
  )
}
